import React, { useState, forwardRef, useImperativeHandle } from 'react';
import SaveEllipseData from './SaveEllipseData';

/**
 * EditEllipse
 * Props:
 * - ellipseInfo: object
 * - setEllipseInfo: func
 * - setImageUrl: func
 * - setCheckedEllipse: func
 * - handleChecked: func
 */
const EditEllipse = forwardRef(function EditEllipse({ ellipseInfo = {}, setEllipseInfo, setImageUrl, setCheckedEllipse, handleChecked }, ref) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editFormData, setEditFormData] = useState({
    label: '',
    center_x: '',
    center_y: '',
    major_axis: '',
    minor_axis: '',
    angle: ''
  });

  const editEllipse = (targetKey = null) => {
    let key = targetKey;
    let value = targetKey ? ellipseInfo[targetKey] : null;

    if (!key) {
      const selected = Object.entries(ellipseInfo || {}).filter(([, v]) => Number(v.isChecked || 0) === 1);
      if (selected.length !== 1) {
        alert('編集するには1行のみ選択してください。');
        return;
      }
      [key, value] = selected[0];
    }

    if (!value) {
      alert('編集対象が見つかりませんでした。');
      return;
    }

    setEditingKey(key);
    setEditFormData({
      label: value.label || '',
      center_x: value.center[0].toString(),
      center_y: value.center[1].toString(),
      major_axis: value.major_axis.toString(),
      minor_axis: value.minor_axis.toString(),
      angle: value.angle.toString()
    });
    setIsEditModalOpen(true);
  };

  useImperativeHandle(ref, () => ({
    openEditForKey: (key) => editEllipse(key)
  }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      const updatedEllipseData = {
        key: editingKey,
        label: editFormData.label,
        center: [parseFloat(editFormData.center_x), parseFloat(editFormData.center_y)],
        major_axis: parseFloat(editFormData.major_axis),
        minor_axis: parseFloat(editFormData.minor_axis),
        angle: parseFloat(editFormData.angle),
        contour: ellipseInfo[editingKey].contour,
      };

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${apiUrl}/update-ellipse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ellipse_data: updatedEllipseData, all_ellipses: ellipseInfo })
      });

      if (!response.ok) throw new Error('楕円の更新に失敗しました');

      const result = await response.json();
      if (result && result.updated_ellipses) {
        setEllipseInfo(result.updated_ellipses);
        if (typeof setImageUrl === 'function') setImageUrl(result.updated_image_url);
        if (typeof setCheckedEllipse === 'function') setCheckedEllipse(Object.entries(result.updated_ellipses).filter(([, v]) => Number(v.isChecked || 0) === 1));
        setIsEditModalOpen(false);
        alert('楕円情報が更新されました');
      } else {
        throw new Error('server returned unexpected response');
      }
    } catch (err) {
      console.error('Error updating ellipse:', err);
      alert('楕円の更新中にエラーが発生しました: ' + (err.message || err));
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="w-full flex flex-row space-x-4 mt-4">
      <button
        className="btn btn--primary"
        onClick={() => {
          setCheckedEllipse(Object.entries(ellipseInfo).filter(([, v]) => Number(v.isChecked || 0) === 1));
          if (typeof handleChecked === 'function') handleChecked();
        }}
      >選択した楕円のみを表示</button>

      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">楕円情報を編集</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">ラベル名</label>
                <input type="text" name="label" value={editFormData.label} onChange={handleInputChange} className="w-full px-3 py-2 border rounded" disabled={isUpdating} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">中心座標 X</label>
                  <input type="number" name="center_x" value={editFormData.center_x} onChange={handleInputChange} step="0.01" className="w-full px-3 py-2 border rounded" disabled={isUpdating} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">中心座標 Y</label>
                  <input type="number" name="center_y" value={editFormData.center_y} onChange={handleInputChange} step="0.01" className="w-full px-3 py-2 border rounded" disabled={isUpdating} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">長軸</label>
                <input type="number" name="major_axis" value={editFormData.major_axis} onChange={handleInputChange} step="0.01" className="w-full px-3 py-2 border rounded" disabled={isUpdating} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">短軸</label>
                <input type="number" name="minor_axis" value={editFormData.minor_axis} onChange={handleInputChange} step="0.01" className="w-full px-3 py-2 border rounded" disabled={isUpdating} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">角度</label>
                <input type="number" name="angle" value={editFormData.angle} onChange={handleInputChange} step="0.01" className="w-full px-3 py-2 border rounded" disabled={isUpdating} />
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button onClick={() => setIsEditModalOpen(false)} className="btn btn--gray" disabled={isUpdating}>キャンセル</button>
              <button onClick={handleUpdate} className="btn btn--primary" disabled={isUpdating}>{isUpdating ? '更新中...' : '更新'}</button>
            </div>
          </div>
        </div>
      )}
      <SaveEllipseData
        ellipseInfo={Object.fromEntries(
          Object.entries(ellipseInfo).filter(([, v]) => Number(v.isChecked || 0) === 1)
        )}
      />
    </div>
  );
});

export default EditEllipse;
